// FK-by-name relation inference.
//
// Strategy: for each table T, look at every column C ending in `Id`/`ID`/`_id`
// whose prefix names another table. Three matching passes from strict→loose:
//   1. Exact normalized prefix == normalized table name (e.g. ArtistId → Artist)
//   2. Exact normalized prefix == any singularized core token of a table name
//      (e.g. customer_id → olist_customers_dataset, because "customer" is a
//       core token of that table after stripping stopwords like olist/dataset)
//   3. All tokens of the prefix appear in a single table's core tokens
//      (e.g. customer_unique_id → olist_customers_dataset, prefix tokens
//       [customer, unique] both present? if multiple matches, prefer the one
//       with the most token overlap)

export interface InferTableInput {
	name: string;
	columns: Array<{ name: string }>;
}

export interface InferredRelation {
	sourceTable: string;
	sourceColumn: string;
	targetTable: string;
	targetColumn: string;
}

const FK_SUFFIX = /^(.+?)(?:Id|ID|_id)$/;

// Words that appear in table names but don't carry meaning for FK matching.
// Add aggressively-common file-naming noise.
const STOPWORDS = new Set([
	'olist',
	'dataset',
	'csv',
	'data',
	'tbl',
	'table',
	'fact',
	'dim',
	'dimension',
	'raw',
	'final',
	'staging',
	'stg',
]);

function normalize(name: string): string {
	return name.toLowerCase().replace(/[_\s-]/g, '');
}

function singularize(name: string): string {
	if (name.length < 4) return name;
	if (/ies$/i.test(name)) return name.replace(/ies$/i, 'y');
	if (/ses$/i.test(name)) return name.replace(/es$/i, '');
	if (/[^s]s$/.test(name)) return name.slice(0, -1);
	return name;
}

/** Split a name into core lowercase singular tokens, dropping stopwords. */
function coreTokens(name: string): string[] {
	return name
		.toLowerCase()
		.split(/[_\s-]+/)
		.map((t) => t.trim())
		.filter((t) => t.length > 0 && !STOPWORDS.has(t))
		.map((t) => singularize(t));
}

export function inferRelations(tables: InferTableInput[]): InferredRelation[] {
	if (tables.length < 2) return [];

	// Pass-1 index: full normalized name (and singularized form)
	const tableByVariant = new Map<string, string>();
	// Pass-2 index: each core token → list of tables that have it
	const tableByToken = new Map<string, string[]>();
	const coreTokensByTable = new Map<string, string[]>();

	for (const t of tables) {
		tableByVariant.set(normalize(t.name), t.name);
		tableByVariant.set(normalize(singularize(t.name)), t.name);
		const tokens = coreTokens(t.name);
		coreTokensByTable.set(t.name, tokens);
		for (const tok of tokens) {
			const list = tableByToken.get(tok) ?? [];
			list.push(t.name);
			tableByToken.set(tok, list);
		}
	}

	const tableByName = new Map(tables.map((t) => [t.name, t]));
	const out: InferredRelation[] = [];
	const seen = new Set<string>();

	for (const t of tables) {
		for (const c of t.columns) {
			const m = c.name.match(FK_SUFFIX);
			if (!m) continue;
			const prefix = m[1];
			if (!prefix) continue;

			const targetTableName = resolveTarget(
				prefix,
				t.name,
				tableByVariant,
				tableByToken,
				coreTokensByTable,
			);
			if (!targetTableName) continue;

			const target = tableByName.get(targetTableName)!;
			const targetColumn = pickPrimaryKey(target, prefix);
			if (!targetColumn) continue;

			const key = `${t.name}.${c.name}->${targetTableName}.${targetColumn}`;
			if (seen.has(key)) continue;
			seen.add(key);

			out.push({
				sourceTable: t.name,
				sourceColumn: c.name,
				targetTable: targetTableName,
				targetColumn,
			});
		}
	}

	return out;
}

function resolveTarget(
	prefix: string,
	sourceTable: string,
	tableByVariant: Map<string, string>,
	tableByToken: Map<string, string[]>,
	coreTokensByTable: Map<string, string[]>,
): string | null {
	// Pass 1: exact normalized prefix match
	const direct = tableByVariant.get(normalize(prefix));
	if (direct && direct !== sourceTable) return direct;

	// Pass 2: prefix is a single core token of one table
	const prefixSingular = singularize(prefix.toLowerCase());
	const byToken = tableByToken.get(prefixSingular);
	if (byToken) {
		const candidates = byToken.filter((n) => n !== sourceTable);
		if (candidates.length === 1) return candidates[0];
		// Multiple matches — pick the one whose tokens are closest in count
		// (prefer focused tables like "customer" over noisy "customer_orders_*")
		if (candidates.length > 1) {
			candidates.sort(
				(a, b) =>
					(coreTokensByTable.get(a)?.length ?? 99) -
					(coreTokensByTable.get(b)?.length ?? 99),
			);
			return candidates[0];
		}
	}

	// Pass 3: prefix has multiple tokens; find a table that contains all of them
	const prefixTokens = coreTokens(prefix);
	if (prefixTokens.length > 1) {
		let best: { name: string; overlap: number } | null = null;
		for (const [name, tokens] of coreTokensByTable.entries()) {
			if (name === sourceTable) continue;
			const tokenSet = new Set(tokens);
			const overlap = prefixTokens.filter((t) => tokenSet.has(t)).length;
			if (overlap === prefixTokens.length) {
				// Full coverage — perfect match
				if (!best || overlap > best.overlap) best = { name, overlap };
			}
		}
		if (best) return best.name;
	}

	return null;
}

function pickPrimaryKey(
	target: InferTableInput,
	fkPrefix: string,
): string | null {
	const names = target.columns.map((c) => c.name);
	const exactFk = names.find((n) =>
		new RegExp(`^${escapeRegex(fkPrefix)}(Id|ID|_id)$`).test(n),
	);
	if (exactFk) return exactFk;
	const tableSelfFk = names.find((n) =>
		new RegExp(`^${escapeRegex(target.name)}(Id|ID|_id)$`, 'i').test(n),
	);
	if (tableSelfFk) return tableSelfFk;
	const justId = names.find((n) => /^id$/i.test(n));
	if (justId) return justId;
	return null;
}

function escapeRegex(s: string): string {
	return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
